import React, { useState, useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import moment from 'moment/moment'
import Slider from 'react-slick/lib'
import { getCruiseTourDetails } from '../../reducers/packageDetails'

import { PrevArrow, NextArrow } from '../elements/CarouselArrows'
import EQuotePackageItineraryModal from './EQuotePackageItineraryModal'
import EQuotePackageItineraryPrint from './EQuotePackageItineraryPrint'

import { filterImagesByFileName } from '../../utils/filterImagesByFileName'

import '../../styles/scss/components/equote-preview-package-itinerary.scss'

//Import Default Images
import cel_atSea from '../../assets/images/defaults/atSea/cel_at_sea_persian_gardens.jpg'
import rci_atSea from '../../assets/images/defaults/atSea/rci_at_sea_sunset_lounge.jpg'

import cel_port from '../../assets/images/defaults/ports/cel_generic_port.jpg'
import rci_port from '../../assets/images/defaults/ports/rci_generic_port.jpg'

const renderNumberOfSlidesToShow = () => {
  if (window.innerWidth > 1200) {
    return 3
  }
  if (window.innerWidth > 1000) {
    return 2
  }
  if (window.innerWidth > 768) {
    return 2
  }
  return 1
}

const renderDayName = name => {
  if (name.length < 40) {
    return name
  }
  return name.slice(0, 40) + '...'
}

const filterInitialSlide = (itinerary, type) => {
  return type === 'cruise'
    ? itinerary.map(day => day.activity).indexOf('EMBARK')
    : itinerary.map(day => day.flag).indexOf(type)
}

const EQuotePackageItinerary = props => {
  const slider = useRef(null)

  const { cruiseTourDetails } = props
  const [numberOfSlidesToShow, setSlideCount] = useState(() =>
    renderNumberOfSlidesToShow()
  )

  const settings = {
    lazyLoad: 'progressive',
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: numberOfSlidesToShow,
    swipeToSlide: true,
    prevArrow: (
      <PrevArrow
        {...props}
        numberOfSlidesToShow={numberOfSlidesToShow}
        large
        gaText="itinerary-previous-button-click"
        shouldSendGaEvent
      />
    ),
    nextArrow: (
      <NextArrow
        {...props}
        numberOfSlidesToShow={numberOfSlidesToShow}
        large
        gaText="itinerary-next-button-click"
        shouldSendGaEvent
      />
    )
  }

  useEffect(() => {
    const handleResize = () => {
      setSlideCount(() => renderNumberOfSlidesToShow())
    }
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const renderBackgroundImage = day => {
    const image = day.highlight
      ? filterImagesByFileName(
          day.highlight.image,
          'itineraries-and-destinations'
        )
      : null

    if (image && image[0] && image[0].displayImage) {
      return image[0].displayImage
    } else {
      if (day.activity === 'CRUISING' || day.name === 'AT SEA') {
        switch (props.brandClassName) {
          case 'royal':
            return rci_atSea
          case 'celebrity':
            return cel_atSea
          default:
            return ''
        }
      } else {
        switch (props.brandClassName) {
          case 'royal':
            return rci_port
          case 'celebrity':
            return cel_port
          default:
            return ''
        }
      }
    }
  }

  const renderSlides = () => {
    return props.itinerary.map((day, i) => {
      return (
        <div key={day.code + i}>
          <div
            className={`equote-preview-package-itinerary-day ${
              props.brandClassName
            }`}
          >
            <div
              className={`equote-preview-package-itinerary-day--header ${
                props.brandClassName
              }`}
            >
              <p className="equote-preview-package-itinerary-day--header--day-text">
                Day {day.day}
              </p>
              <p className="equote-preview-package-itinerary-day--header--date">
                {moment(day.displayDate, 'YYYY-MM-DD').format('MMMM DD, YYYY')}
              </p>
              {day.flag && (
                <div className="equote-preview-package-itinerary-day--header--cruise-tour-flag">
                  <p>{day.flag}</p>
                </div>
              )}
            </div>
            <div className="equote-preview-package-itinerary-day--sub-header">
              <p className="equote-preview-package-itinerary-day--sub-header-arrival">
                {day.arrivalTime && `Arrives: `}
                <span>
                  {day.arrivalTime &&
                    `${moment(day.arrivalTime, 'kk:mm:ss').format('h:mm a')}`}
                </span>
              </p>
              <p className="equote-preview-package-itinerary-day--sub-header-arrival">
                {day.departureTime && `Departs: `}
                <span>
                  {day.departureTime &&
                    `${moment(day.departureTime, 'kk:mm:ss').format('h:mm a')}`}
                </span>
              </p>
            </div>
            <div
              className="equote-preview-package-itinerary-day--image-container"
              style={{
                backgroundImage: 'url(' + renderBackgroundImage(day) + ')',
                width: '100%'
              }}
            />
            <div className="equote-preview-package-itinerary-day--description">
              <p className="equote-preview-package-itinerary-day--description--title">
                {day.name && renderDayName(day.name)}
              </p>
              {/* dangerouslySetInnerHTML can be changed if the endpoint removed the <p>'s */}
              <div
                className="equote-preview-package-itinerary-day--description--description"
                dangerouslySetInnerHTML={{
                  __html: day.highlight.description
                }}
              />
            </div>
          </div>
        </div>
      )
    })
  }

  const { itinerary } = props
  return (
    <div className="equote-preview-package-itinerary" id="itinerary">
      <div className="equote-preview-package-itinerary--header u-content-container">
        <h1 className={`section-title ${props.brandClassName}`}>
          Itinerary and Destination
        </h1>

        <div className="equote-preview-package-itinerary--header--links">
          {props.startDate && (
            <div className="equote-preview-package-itinerary--header--links--detail">
              Sailing{' '}
              {moment(props.startDate, 'YYYY-MM-DD').format('MMMM DD, YYYY')}
            </div>
          )}
          {cruiseTourDetails && (
            <div className="equote-preview-package-itinerary--header--links--wrapper">
              <div className="equote-preview-package-itinerary--header--links--group">
                {Boolean(
                  cruiseTourDetails.preTourNights &&
                    itinerary.some(day => day.flag === 'Pre-Cruisetour')
                ) && (
                  <>
                    <div
                      onClick={() => {
                        slider.current.slickGoTo(
                          filterInitialSlide(itinerary, 'Pre-Cruisetour')
                        )
                      }}
                      className="equote-preview-package-itinerary--header--link"
                    >
                      Pre-Cruise
                    </div>{' '}
                    <span className="divisor">|</span>
                  </>
                )}
                <div
                  onClick={() => {
                    slider.current.slickGoTo(
                      filterInitialSlide(itinerary, 'cruise')
                    )
                  }}
                  className="equote-preview-package-itinerary--header--link"
                >
                  Cruise Itinerary
                </div>{' '}
                {Boolean(
                  cruiseTourDetails.postTourNights &&
                    itinerary.some(day => day.flag === 'Post-Cruisetour')
                ) && (
                  <>
                    <span className="divisor">|</span>
                    <div
                      onClick={() => {
                        slider.current.slickGoTo(
                          filterInitialSlide(itinerary, 'Post-Cruisetour')
                        )
                      }}
                      className="equote-preview-package-itinerary--header--link"
                    >
                      Post Cruise
                    </div>{' '}
                  </>
                )}
              </div>
            </div>
          )}
          <div className="equote-preview-package-itinerary--header--links--wrapper u-flex-container--justify-end">
            <div className="equote-preview-package-itinerary--header--links--group">
              <EQuotePackageItineraryModal
                {...props}
                contentLabel="Itinerary Details Modal"
              />
            </div>
          </div>
        </div>
        <EQuotePackageItineraryPrint {...props} />
      </div>

      {props.itinerary && props.itinerary.length && (
        <Slider ref={slider} {...settings}>
          {renderSlides(props)}
        </Slider>
      )}
    </div>
  )
}

const mapStateToProps = (state, ownProps) => {
  const { activePackage } = ownProps
  const brandCode = activePackage && activePackage.brand.toLowerCase()

  const destinationCode = activePackage && activePackage.destinationCode

  const destinationDetails =
    destinationCode &&
    state.destinations &&
    state.destinations.items &&
    state.destinations.items.find(
      destination => destination.destination_code === destinationCode
    )
  const destinationImageSource =
    destinationDetails && brandCode && destinationDetails[`image_${brandCode}`]
      ? destinationDetails[`image_${brandCode}`]
      : destinationDetails && destinationDetails.image

  const destinationImage =
    destinationImageSource &&
    filterImagesByFileName(destinationImageSource, 'details-hero')

  return {
    startDate: activePackage ? activePackage.sailDate : null,
    cruiseTourDetails: getCruiseTourDetails(state),
    destinationImage,
    landPrograms: activePackage.landPrograms
  }
}

export default connect(mapStateToProps)(EQuotePackageItinerary)
